'use client';

/**
 * InterceptCard — ONE dense transmission in THE INTERCEPTS feed.
 *
 * Visual source of truth: docs/design/intercepts-prototype/intercepts.html (.icpt)
 * + intercepts-v2.html (SIGNAL pill .sig, sentiment hairline). Token-native
 * (ink/surface/divider/amber/parchment), gold-only.
 *
 * Density rules (the cure for "one tweet per viewport"):
 *  - 24px avatar; handle + entity chip + relative time on ONE line
 *  - body text VERBATIM, 4-line clamp with click-to-expand
 *  - media as thumbnail TILES (max-h 140px, natural aspect) — never full-width heroes
 *  - engagement footer mono 10px; hairline dividers between cards
 *  - SIGNAL grade pill (S/A/B/C) when a rating exists
 *  - provenance badge "◆ CITED IN THE BRIEF" top-right, stacked above the timestamp
 */

import { useState } from 'react';
import type { Tweet } from '@/types/twitter';
import type { Tag } from '@/types/tags';
import { ENTITY_TYPES, initials, type EntityTypeKey } from '@/components/dashboard/format';

/** SIGNAL grade derived from tweet_ratings.newsworthiness (0–100). */
export type SignalGrade = 'S' | 'A' | 'B' | 'C';
export function gradeFor(newsworthiness: number): SignalGrade {
  if (newsworthiness >= 85) return 'S';
  if (newsworthiness >= 70) return 'A';
  if (newsworthiness >= 50) return 'B';
  return 'C';
}

export interface InterceptCardProps {
  tweet: Tweet;
  profileImage?: string;
  linkedTags?: Tag[];
  /** newsworthiness 0–100 from tweet_ratings; undefined ⇒ unrated (no pill). */
  newsworthiness?: number;
  /**
   * DESK NOTE — the analyst's one-liner on this transmission
   * (tweet_ratings.summary, headline fallback). Rendered on S/A grades only:
   * the top of the SIGNAL-sorted tape reads judged, the tail stays raw.
   */
  deskNote?: string;
  /** true ⇒ this tweet's id was found in a recap's featuredTweets[] (provenance). */
  cited?: boolean;
  /** Hide the entity chip (focus mode — the focus header already names the entity). */
  hideEntityTag?: boolean;
  /** Focus-in-place callback for the entity chip. */
  onEntityClick?: (entityId: string, entitySlug: string) => void;
  /** true ⇒ the quoted transmission's author is itself a tracked handle (in-graph edge). */
  quotedIsTracked?: boolean;
}

function fmtNum(n: number | undefined): string {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function relTime(ts: any): string {
  if (!ts) return '';
  const date: Date = ts?.toDate
    ? ts.toDate()
    : ts?.seconds
      ? new Date(ts.seconds * 1000)
      : new Date(ts);
  const diff = Date.now() - date.getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3_600_000);
  const d = Math.floor(diff / 86_400_000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m`;
  if (h < 24) return `${h}h`;
  if (d < 7) return `${d}d`;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/** Strip a trailing t.co link (media/quote shortlink debris) — text stays verbatim otherwise. */
function cleanText(text: string): string {
  return text.replace(/\s*https:\/\/t\.co\/\w+\s*$/, '');
}

const SIG_STYLE: Record<SignalGrade, React.CSSProperties> = {
  S: { borderColor: 'var(--amber-line)', color: 'var(--amber)', background: 'var(--amber-dim)' },
  A: { borderColor: 'rgba(242,242,240,0.25)', color: 'var(--parchment)' },
  B: { borderColor: 'var(--divider)', color: 'var(--parchment-dim)' },
  C: { borderColor: 'var(--divider)', color: 'var(--slate)' },
};

export default function InterceptCard({
  tweet,
  profileImage,
  linkedTags,
  newsworthiness,
  deskNote,
  cited,
  hideEntityTag,
  onEntityClick,
  quotedIsTracked,
}: InterceptCardProps) {
  const [expanded, setExpanded] = useState(false);

  const entity = linkedTags && linkedTags.length > 0 ? linkedTags[0] : null;
  const entityTypeLabel = entity?.entityType
    ? ENTITY_TYPES[entity.entityType as EntityTypeKey]?.label ?? entity.entityType
    : null;

  const grade = typeof newsworthiness === 'number' ? gradeFor(newsworthiness) : null;
  // Self-thread = the author replying to itself → a thread continuation, not a reply to others.
  const isSelfThread = !!tweet.isReply && !!tweet.inReplyToUsername &&
    tweet.inReplyToUsername.toLowerCase() === (tweet.username || '').toLowerCase();
  const allPhotos = (tweet.media ?? []).filter((m) => m.type === 'photo');
  // The X Article cover is promoted into `media` AND shown in the article block above; drop the
  // promoted cover (media[0], the first photo) from the thumbnail tiles to avoid a duplicate image.
  const photos = (tweet.article && tweet.article.coverImage) ? allPhotos.slice(1) : allPhotos;
  const firstNonPhoto = (tweet.media ?? []).find((m) => m.type === 'video' || m.type === 'gif');
  const body = cleanText(tweet.text ?? '');
  const quoted = tweet.quotedTweet;
  // DESK NOTE only on S/A grades — judgment leads the tape, the tail stays raw.
  const showDeskNote = !!deskNote && (grade === 'S' || grade === 'A');

  return (
    <article
      style={{
        breakInside: 'avoid',
        position: 'relative',
        padding: '14px 0 14px 12px',
        borderBottom: '0.5px solid var(--divider)',
        borderLeft: '2px solid transparent',
      }}
    >
      {/* Provenance badge — top-right, STACKED above the timestamp (padding-right on the
          top row reserves the lane so it never collides with the time). */}
      {cited && (
        <span
          style={{
            position: 'absolute',
            top: 14,
            right: 0,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontFamily: 'var(--font-mono)',
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            padding: '3px 8px',
            borderRadius: 3,
            border: '0.5px solid var(--amber-line)',
            color: 'var(--amber)',
            background: 'var(--amber-dim)',
            whiteSpace: 'nowrap',
          }}
          title="This transmission was cited in a published recap"
        >
          <span style={{ fontSize: 8 }}>◆</span>Cited in the brief
        </span>
      )}

      {/* Top line: avatar · handle · @id · entity chip · SIGNAL · time */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          marginBottom: 8,
          flexWrap: 'wrap',
          paddingRight: cited ? 150 : 0,
        }}
      >
        {profileImage ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={profileImage}
            alt=""
            loading="lazy"
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              flexShrink: 0,
              objectFit: 'cover',
              border: '0.5px solid var(--divider)',
            }}
          />
        ) : (
          <span
            style={{
              width: 24,
              height: 24,
              borderRadius: 6,
              flexShrink: 0,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--surface-2)',
              border: '0.5px solid var(--divider)',
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              fontWeight: 700,
              color: 'var(--parchment-dim)',
            }}
          >
            {initials(tweet.username)}
          </span>
        )}

        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 13.5,
            letterSpacing: '-0.01em',
            color: 'var(--parchment)',
            whiteSpace: 'nowrap',
            maxWidth: 180,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {tweet.username}
        </span>
        <a
          href={`https://twitter.com/${tweet.handleId}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--slate)', textDecoration: 'none' }}
        >
          @{tweet.handleId}
        </a>

        {!hideEntityTag && entity && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onEntityClick?.(entity.id, entity.slug);
            }}
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              padding: '2px 7px',
              borderRadius: 3,
              border: '0.5px solid var(--amber-line)',
              color: 'var(--amber)',
              background: 'var(--amber-dim)',
              cursor: 'pointer',
              maxWidth: 140,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={`Focus ${entity.name}`}
          >
            {entityTypeLabel ?? entity.name}
          </button>
        )}

        {grade && (
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.06em',
              flexShrink: 0,
              padding: '2px 7px',
              borderRadius: 3,
              border: '0.5px solid var(--divider)',
              display: 'inline-flex',
              gap: 5,
              alignItems: 'center',
              ...SIG_STYLE[grade],
            }}
            title={`SIGNAL grade ${grade} · newsworthiness ${Math.round(newsworthiness!)}/100`}
          >
            <span style={{ fontSize: 9.5 }}>{grade}</span>
            {Math.round(newsworthiness!)}
          </span>
        )}

        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            color: 'var(--parchment-dim)',
            marginLeft: 'auto',
            flexShrink: 0,
          }}
        >
          {relTime(tweet.createdAt)}
        </span>
      </div>

      {/* Self-thread continuation badge — the author replying to itself reads as a thread,
          not a reply to another account. (True nested threading needs the parent tweet id,
          which we don't capture for these replies yet.) */}
      {isSelfThread && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            marginBottom: 8,
            fontFamily: 'var(--font-mono)',
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--amber-line)',
          }}
        >
          🧵 Thread — {tweet.username} continued
        </div>
      )}

      {/* X Article — cover + label make it unmistakably an article; the body below carries
          the title+preview, and we link out to X for the full original (we report on it, not
          re-host it). */}
      {!tweet.isRetweet && tweet.article && (
        <div style={{ marginBottom: 9 }}>
          {tweet.article.coverImage && (
            // eslint-disable-next-line @next/next/no-img-element -- raw pbs.twimg.com cover, not in next/image domains
            <img
              src={tweet.article.coverImage}
              alt={tweet.article.title || 'Article cover'}
              loading="lazy"
              style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 5, border: '0.5px solid var(--divider)' }}
            />
          )}
          <span
            style={{
              display: 'inline-block',
              marginTop: tweet.article.coverImage ? 7 : 0,
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--amber-line)',
            }}
          >
            📄 Article
          </span>
        </div>
      )}

      {/* Body — verbatim, 4-line clamp, click to expand. */}
      <div
        onClick={() => setExpanded((v) => !v)}
        style={{
          fontSize: 14,
          lineHeight: 1.5,
          color: '#D8D8D4',
          whiteSpace: 'pre-wrap',
          cursor: 'pointer',
          ...(expanded
            ? {}
            : {
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }),
        }}
      >
        {body}
      </div>

      {!tweet.isRetweet && tweet.article?.url && (
        <a
          href={tweet.article.url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            marginTop: 6,
            fontFamily: 'var(--font-mono)',
            fontSize: 11,
            color: 'var(--link, #6AA9FF)',
            textDecoration: 'none',
          }}
          title="Read the full article on X"
        >
          Read full article on X →
        </a>
      )}

      {/* Quoted transmission — tweets.quotedTweet, nested context block. Gold-edged
          when the quoted author is itself tracked (an in-graph edge on the card). */}
      {quoted && (
        <a
          href={quoted.tweetUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            display: 'block',
            marginTop: 9,
            border: '0.5px solid var(--divider)',
            borderLeft: `2px solid ${quotedIsTracked ? 'var(--amber-line)' : 'var(--slate)'}`,
            borderRadius: 5,
            background: 'var(--surface)',
            padding: '9px 12px',
            textDecoration: 'none',
          }}
          title="View the quoted post on X"
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span
              style={{
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                fontSize: 12,
                letterSpacing: '-0.01em',
                color: 'var(--parchment)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 160,
              }}
            >
              @{quoted.username}
            </span>
            {quotedIsTracked && (
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 8,
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  padding: '1px 6px',
                  borderRadius: 3,
                  border: '0.5px solid var(--amber-line)',
                  color: 'var(--amber)',
                  background: 'var(--amber-dim)',
                }}
              >
                Tracked
              </span>
            )}
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--slate)', marginLeft: 'auto', flexShrink: 0 }}>
              {relTime(quoted.createdAt)}
            </span>
          </span>
          <span
            style={{
              fontSize: 12.5,
              lineHeight: 1.45,
              color: 'rgba(242,242,240,0.7)',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {cleanText(quoted.text ?? '')}
          </span>
        </a>
      )}

      {/* Media — thumbnail tiles (max-h 140), natural aspect. NEVER full-width hero. */}
      {(photos.length > 0 || firstNonPhoto) && (
        <div style={{ display: 'flex', gap: 6, marginTop: 9, overflow: 'hidden' }}>
          {photos.slice(0, 3).map((m, i) => (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              key={`${m.url}-${i}`}
              src={m.url}
              alt=""
              loading="lazy"
              style={{
                maxHeight: 140,
                width: 'auto',
                maxWidth: 220,
                borderRadius: 5,
                border: '0.5px solid var(--divider)',
                objectFit: 'cover',
                flexShrink: 0,
              }}
            />
          ))}
          {photos.length === 0 && firstNonPhoto && (
            <a
              href={tweet.tweetUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{
                height: 140,
                width: 200,
                borderRadius: 5,
                border: '0.5px solid var(--divider)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                background: firstNonPhoto.thumbnailUrl
                  ? `center / cover no-repeat url(${firstNonPhoto.thumbnailUrl})`
                  : 'var(--surface-2)',
                position: 'relative',
                textDecoration: 'none',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 9,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'rgba(255,255,255,0.85)',
                  background: 'rgba(0,0,0,0.55)',
                  padding: '4px 8px',
                  borderRadius: 3,
                }}
              >
                {firstNonPhoto.type === 'gif' ? 'GIF ▷' : 'Watch ▷'}
              </span>
            </a>
          )}
        </div>
      )}

      {/* DESK NOTE — the analyst's one-liner from tweet_ratings (S/A grades only):
          the judgment, in the desk's own words, directly on the raw signal. */}
      {showDeskNote && (
        <div
          style={{
            marginTop: 9,
            padding: '7px 11px 8px',
            borderLeft: '2px solid var(--amber-line)',
            background: 'rgba(232,168,50,0.05)',
            borderRadius: '0 4px 4px 0',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 8.5,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--amber)',
              marginRight: 8,
            }}
          >
            Desk
          </span>
          <span style={{ fontSize: 12.5, lineHeight: 1.45, color: 'var(--parchment-dim)' }}>{deskNote}</span>
        </div>
      )}

      {/* Engagement footer — mono 10px. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginTop: 10,
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '0.04em',
          color: 'var(--slate)',
        }}
      >
        <span>
          <b style={{ color: 'var(--parchment-dim)', fontWeight: 600 }}>{fmtNum(tweet.metrics?.replies)}</b> replies
        </span>
        <span>
          <b style={{ color: 'var(--parchment-dim)', fontWeight: 600 }}>{fmtNum(tweet.metrics?.retweets)}</b> RT
        </span>
        <span>
          <b style={{ color: 'var(--parchment-dim)', fontWeight: 600 }}>{fmtNum(tweet.metrics?.likes)}</b> likes
        </span>
        <a
          href={tweet.tweetUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{
            marginLeft: 'auto',
            color: 'rgba(242,242,240,0.35)',
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
          title="View on X"
        >
          View on X ↗
        </a>
      </div>
    </article>
  );
}
